import api from '../service/api.service';
import { parseBankingTransactionFilters } from '../../helpers/parseQueryFilters';
import { parseQuerySortFields } from '../../helpers/parseQuerySortFields';
import {
  FilterTypes,
  ActiveSortField,
  FirmTransactionsResultSet,
  TransactionQueryParams,
} from '../../types';

import {
  AutoSweepAmount,
  AutoSweepBatchDetailsResultSet,
  AutoSweepConfig,
  AutoSweepTransactionDetailsResultSet,
  AutoSweepTransactionListParams,
  AutoSweepTransactionListResultSet,
  AutoSweepTrigger,
} from '../../views/FirmBankingView/types';

const BASE_URL_NEW = 'v1/fo/banking';

export const getFirmTransactions = async ({
  filters,
  activeSortField,
  match,
}: TransactionQueryParams) => {
  const queryFilters = parseBankingTransactionFilters(filters as FilterTypes);
  const sortFilters = parseQuerySortFields(activeSortField as ActiveSortField);
  const targetUrl = `${BASE_URL_NEW}/transactions?match=${match || ''
    }${queryFilters}${sortFilters}`;

  return await api.get<FirmTransactionsResultSet>(targetUrl);
};

const AUTO_SWEEP_CONFIG_BASE_URL = '/v1/firms/banking/auto-sweep-config';
const getAutoSweepTransactionsBaseUrl = (firmId: number) =>
  `/v1/firms/${firmId}/banking/auto-sweep-transactions`;
const getAutoSweepBatchesBaseUrl = (firmId: number) =>
  `/v1/firms/${firmId}/banking/auto-sweep-batches`;

export const getAutoSweepConfig = async (data: { id?: number; sweepAccountType?: string }) => {
  const targetUrl = `${AUTO_SWEEP_CONFIG_BASE_URL}`;

  return await api.get<AutoSweepConfig>(targetUrl, { params: data });
};

export const updateAutoSweepConfig = async (
  data: {
    id?: number;
    sweepAccountType?: string;
    enabled?: boolean;
    destinationAccountId?: number;
    trigger?: AutoSweepTrigger;
    amount?: AutoSweepAmount;
    retryDelayMinutes?: number;
    maxRetries?: number;
  }
) => {
  const targetUrl = `${AUTO_SWEEP_CONFIG_BASE_URL}`;
  const { id, sweepAccountType, ...requestData } = data;
  return await api.put(targetUrl, requestData, {
    params: { id, sweepAccountType }
  });
};

export const getAutoSweepTransactionList = async ({
  firmId,
  page = 1,
  size = 20,
  accountType,
  status,
}: AutoSweepTransactionListParams) => {
  const targetUrl = `${getAutoSweepTransactionsBaseUrl(firmId)}?page=${page}&size=${size}${accountType ? `&accountType=${accountType}` : ''
    }${status ? `&status=${status}` : ''}`;

  return await api.get<AutoSweepTransactionListResultSet>(targetUrl);
};

export const getAutoSweepTransactionDetails = async (
  firmId: number,
  id: number | string,
) => {
  const targetUrl = `${getAutoSweepTransactionsBaseUrl(firmId)}/${id}`;

  return await api.get<AutoSweepTransactionDetailsResultSet>(targetUrl);
};

export const getAutoSweepBatchDetails = async (
  firmId: number,
  batchCode: string,
  page = 1,
  size = 50,
  search?: string,
  contributionType?: 'settlement' | 'attorneyFee' | 'deduction',
  contributionSort?: 'dateDesc' | 'dateAsc' | 'amountDesc' | 'amountAsc',
) => {
  const query = new URLSearchParams({ page: String(page), size: String(size) });
  if (search) query.set('search', search);
  if (contributionType) query.set('contributionType', contributionType);
  if (contributionSort) query.set('contributionSort', contributionSort);
  const targetUrl = `${getAutoSweepBatchesBaseUrl(firmId)}/${encodeURIComponent(batchCode)}?${query.toString()}`;
  return await api.get<AutoSweepBatchDetailsResultSet>(targetUrl);
};

export const exportAutoSweepTransactions = async ({
  firmId,
  accountType,
  status,
  batchCode,
  search,
  contributionSort,
}: {
  firmId: number;
  accountType?: string;
  status?: string;
  batchCode?: string;
  search?: string;
  contributionSort?: 'dateDesc' | 'dateAsc' | 'amountDesc' | 'amountAsc';
}) => {
  const query = new URLSearchParams();
  if (accountType) query.set('accountType', accountType);
  if (status) query.set('status', status);
  if (batchCode) query.set('batchCode', batchCode);
  if (search) query.set('search', search);
  if (contributionSort) query.set('contributionSort', contributionSort);
  const targetUrl = `${getAutoSweepTransactionsBaseUrl(firmId)}/export?${query.toString()}`;
  return api.post<Blob>(targetUrl, {}, { responseType: 'blob' });
};

export const retryTransaction = async (firmId: number, transactionId: number) => {
  const targetUrl = `${getAutoSweepTransactionsBaseUrl(firmId)}/${transactionId}/retry`;

  return await api.put<{ success: boolean; message: string }>(targetUrl);
};